<template>
  <div>
    <div
      v-if="tagDuplicates.length > 0 && !allowDuplicates"
      class="mb-2 tag-validation-banner p-1 d-flex w-100"
    >
      <div class="pr-2">
        <RsIcon
          class="icon-color-error"
          icon="circle-exclamation"
          weight="fas"
        />
      </div>
      <div class="flex-fill">
        Tag(s) already exist: <strong>{{ tagDuplicates.join(', ') }}</strong>
      </div>
    </div>
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup></label>
    <VoerroTagsInput
      :id="id"
      :key="value.length"
      :value="value"
      :placeholder="placeholder"
      :typeahead="typeahead"
      :limit="limit"
      :add-tags-on-space="addTagsOnSpace"
      :add-tags-on-comma="addTagsOnComma"
      :add-tags-on-blur="addTagsOnBlur"
      :disabled="disabled"
      :allow-duplicates="true"
      @change="handleSearch"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import VoerroTagsInput from '@voerro/vue-tagsinput';

export type TVoerroTagsInputVal = { key: string, value: string };

export const formatVoerroInput = (input: TVoerroTagsInputVal) => input.value
  .split(',')
  .reduce<TVoerroTagsInputVal[]>((acc, cur) => [
  ...acc,
  ...cur.trim().length > 0 ? [{ key: cur, value: cur.trim() }] : [],
], []);

export default defineComponent({
  name: 'RsFormTagsInputV2',
  components: { VoerroTagsInput },

  props: {
    /**
     * Main form element ID attribute
     */
    id: {
      type: String,
      default: 'rs-form-tags-input',
    },

    /**
     * Form Control label
     */
    label: {
      type: String,
    },

    /**
     * Adds styling if input is required (DOES NOT VALIDATE)
     */
    required: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables input
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows typeahead on the control when existing tags were selected.
     */
    typeahead: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies a limit to amount of tags that can be added
     */
    limit: {
      type: Number,
      default: 0,
    },

    /**
     * Allows adding tags when spacebar is hit.
     */
    addTagsOnSpace: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows adding tags when comma key is hit.
     */
    addTagsOnComma: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows adding tags when we blur the control.
     */
    addTagsOnBlur: {
      type: Boolean,
      default: false,
    },

    /**
     * Text value for input placeholder attribute
     */
    placeholder: {
      type: String,
    },

    /**
     * Existing tags must be formatted to `TVoerroTagsInputVal[]`.
     * To track changes, use the `:value` prop and the `@on-tags-changed`
     * event to get the same structure output.
     */
    value: {
      type: Array as PropType<TVoerroTagsInputVal[]>,
      default: () => [],
    },

    /**
     * If duplicate tag names are allowed. This is managed by this component
     * and not the Voerro one we wrap with this.
     */
    allowDuplicates: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tagDuplicates: [] as string[],
    };
  },

  methods: {
    /**
     * When event's value param has a length greater than our given value prop state, we
     * determine existence of the newly added tag in our current state and if we are allowing
     * duplicate tag names or not.
     *
     * The end result is notifying user of the tag duplicates if they aren't allowed,
     * and adding any non-duplicate tags the payload we want to emit.
     *
     * If the above length condition is less than or equal to value prop state,
     * we emit the new value, as something has been removed.
     *
     * Emits `@on-tags-changed` event once processing is done.
     */
    handleInput(newValues: TVoerroTagsInputVal[]) {
      if (JSON.stringify(newValues) !== JSON.stringify(this.value)) {
        if (newValues.length > this.value.length) {
          const addedValue = newValues[newValues.length - 1];
          const formatted = formatVoerroInput(addedValue ?? { value: '', key: '' });
          const newUniqueTags = new Set(formatted
            .filter((f) => this.value.some((c) => c.value === f.value))
            .map((v) => v.value));

          this.tagDuplicates = Array.from(newUniqueTags);

          const tagsForOutput = [
            ...this.value,
            ...(this.allowDuplicates
              ? formatted
              : formatted.filter((f) => !this.tagDuplicates.some((td) => td.toLowerCase() === f.value.toLowerCase()))
            ),
          ];

          this.$emit('on-tags-changed', tagsForOutput);
          return;
        }

        this.$emit('on-tags-changed', newValues);
      }
    },

    /**
     * When input search field is typed, emits `@on-search` event.
     */
    handleSearch(e: string) {
      this.$emit('on-search', e);
    },

    /**
     * When input search field is blurred, emits `@on-blur` event.
     */
    handleBlur(e: FocusEvent) {
      this.$emit('on-blur', e);
    },

    /**
     * When input search field is focused, emits `@on-focus` event.
     */
    handleFocus(e: FocusEvent) {
      this.tagDuplicates = [];
      this.$emit('on-focus', e);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

/* The input */
.tags-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.tags-input input {
  flex: 1;
  background: transparent;
  border: none;
}

.tags-input input:focus {
  outline: none;
}

.tags-input input[type="text"] {
  color: #495057;
}

.tags-input-wrapper-default {
  padding: .5em .25em;

  background: #fff;

  border: 1px solid transparent;
  border-radius: .25em;
  border-color: #dbdbdb;
}

.tags-input-wrapper-default.active {
  border: 1px solid #8bbafe;
  box-shadow: 0 0 0 0.2em rgba(13, 110, 253, 0.25);
  outline: 0 none;
}

/* The tag badges & the remove icon */
.tags-input span {
  margin: 0.3em 0.3em;
}

.tags-input-remove {
  cursor: pointer;
  position: absolute;
  display: inline-block;
  right: 0.3em;
  top: 0.3em;
  padding: 0.5em;
  overflow: hidden;
}

.tags-input-remove:focus {
  outline: none;
}

.tags-input-remove:before, .tags-input-remove:after {
  content: '';
  position: absolute;
  width: 75%;
  left: 0.15em;
  background: #fff;
  height: 2px;
  margin-top: -1px;
}

.tags-input-remove:before {
  transform: rotate(45deg);
}
.tags-input-remove:after {
  transform: rotate(-45deg);
}

/* Tag badge styles */
.tags-input-badge {
  position: relative;
  display: inline-block;
  padding: 0.35em 0.4em;
  font-size: 75%;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25em;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tags-input-badge-pill {
  padding-right: 1.25em;
  padding-left: 0.6em;
  border-radius: 0.4em;
}
.tags-input-badge-pill.disabled {
  padding-right: 0.6em;
}

.tags-input-badge-selected-default {
  color: #ffff;
  background-color: #7d59e1;
}

/* Typeahead */
.typeahead-hide-btn {
  color: #999 !important;
  font-style: italic;
}

/* Typeahead - badges */
.typeahead-badges > span {
  margin-top: .5em;
}

.typeahead-badges > span {
  cursor: pointer;
  margin-right: 0.3em;
}

/* Typeahead - dropdown */
.typeahead-dropdown {
  list-style-type: none;
  padding: 0;
  margin: 0;
  position: absolute;
  width: 100%;
  z-index: 1000;
}

.typeahead-dropdown li {
  padding: .25em 1em;
  cursor: pointer;
}

/* Typeahead elements style/theme */
.tags-input-typeahead-item-default {
  color: #fff;
  background-color: #343a40;
}

.tags-input-typeahead-item-highlighted-default {
  color: #fff;
  background-color: #007bff !important;
}

.tag-validation-banner {
  color: $global-text-color;
  justify-content: space-evenly;
  background-color: $error-light;
  border: 1px solid $error-dark;
  border-radius: $input-radius;
  width: fit-content;

  .icon-color-error {
    color: $error-dark;
  }
}
</style>
